import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { apiError, setToken, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, Upload, Check, X, ShieldCheck, BadgeCheck, FileText, Wrench, HeartHandshake } from "lucide-react";
import { JOIN_RESUME_KEY } from "@/components/JoinResume";
import { shrinkImage } from "@/lib/image";


const DOC_DEFS = [
    { type: "photo", label: "Profile Photo", required: true, accept: "image/*", hint: "A clear face photo" },
    { type: "govt_id", label: "Government ID", required: true, accept: "image/*,.pdf", hint: "Aadhaar / PAN / Driving License" },
    { type: "skill", label: "Skill / Experience Proof", required: false, accept: "image/*,.pdf", hint: "Certificate or past work (optional)" },
    { type: "police", label: "Police Verification", required: true, accept: "image/*,.pdf", hint: "Police verification document" },
];
const CARE_DOC_DEFS = [
    { type: "photo", label: "Profile Photo", required: true, accept: "image/*", hint: "A clear face photo" },
    { type: "govt_id", label: "Aadhaar / Government ID", required: true, accept: "image/*,.pdf", hint: "Aadhaar preferred" },
    { type: "address_proof", label: "Address Proof", required: true, accept: "image/*,.pdf", hint: "Utility bill / rent agreement / voter ID" },
    { type: "police", label: "Police Verification", required: true, accept: "image/*,.pdf", hint: "Police verification certificate" },
    { type: "care_certificate", label: "Caregiver / Nursing Certificate", required: true, accept: "image/*,.pdf", hint: "GNM / ANM / caregiver training course" },
    { type: "medical_fitness", label: "Medical Fitness Certificate", required: true, accept: "image/*,.pdf", hint: "From a registered doctor" },
    { type: "first_aid", label: "First-aid / CPR Certificate", required: false, accept: "image/*,.pdf", hint: "Optional" },
];
const CARE_SKILLS = [
    { key: "elder_companionship", name: "Elder companionship" }, { key: "patient_care", name: "Patient care" },
    { key: "medication_support", name: "Medication support" }, { key: "post_hospital_care", name: "Post-hospital care" },
    { key: "dementia_care", name: "Dementia care" }, { key: "mobility_assist", name: "Mobility & walking help" },
    { key: "vitals_monitoring", name: "BP / sugar monitoring" }, { key: "night_care", name: "Night care" },
];

export default function JoinPro() {
    const [step, setStep] = useState(1);
    const [params] = useSearchParams();
    const [track, setTrack] = useState(params.get("track") === "care" ? "care" : "services");
    const { user, refresh, logout } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        if (user && user.role === "professional") setStep(2);
    }, [user]);
    const care = track === "care";

    return (
        <div className="mx-auto min-h-screen max-w-md bg-lightbg">
            <div className="rounded-b-3xl bg-navy px-6 pb-7 pt-6 text-white">
                <Logo size={40} light />
                <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight">Join as a <span className={care ? "text-[#FB7185]" : "text-orange"}>{care ? "HOMIIE Caregiver" : "HOMIIE Professional"}</span></h1>
                <p className="mt-2 text-sm text-white/70">{care ? "Support elders and families in Jodhpur with verified, respectful home care." : "Grow your work with verified, steady jobs near you. Get paid securely with HOMIIE."}</p>
                <div className="mt-4 flex gap-2 text-xs">
                    <span className={`rounded-full px-3 py-1 font-semibold ${step === 1 ? "bg-orange text-white" : "bg-white/10 text-white/70"}`}>1 · Account</span>
                    <span className={`rounded-full px-3 py-1 font-semibold ${step === 2 ? "bg-orange text-white" : "bg-white/10 text-white/70"}`}>2 · Details & Documents</span>
                </div>
            </div>

            <div className="px-6 py-6">
                {step === 1 ? <><TrackPicker track={track} setTrack={setTrack} /><AccountStep track={track} onDone={async () => { await refresh(); setStep(2); }} /></> : <DetailsStep onSubmitted={() => nav("/pro")} />}
            </div>

            <p className="pb-10 text-center text-sm text-navy/60">
                {user ? <>Signed in as <b>{user.name}</b> · Not you? <button data-testid="join-logout-link" onClick={async () => { await logout(); nav("/login"); }} className="font-bold text-orange">Log out</button></>
                    : <>Already enrolled? <Link to="/login" data-testid="join-login-link" className="font-bold text-orange">Log in</Link></>}
            </p>
        </div>
    );
}

function TrackPicker({ track, setTrack }) {
    const opts = [
        { k: "services", icon: Wrench, t: "Home Services", d: "Electrician, plumber, AC, appliances…" },
        { k: "care", icon: HeartHandshake, t: "Caregiver (Care)", d: "Elder care, patient care, companionship" },
    ];
    return (
        <div className="mb-5" data-testid="join-track-picker">
            <p className="mb-2 text-sm font-semibold text-navy">I want to join as</p>
            <div className="grid grid-cols-2 gap-2.5">
                {opts.map((o) => (
                    <button key={o.k} type="button" data-testid={`join-track-${o.k}`} onClick={() => setTrack(o.k)}
                        className={`rounded-2xl border-2 p-3 text-left transition-colors ${track === o.k ? (o.k === "care" ? "border-[#F43F5E] bg-rose-50" : "border-orange bg-orange/5") : "border-navy/10 bg-white"}`}>
                        <o.icon size={20} className={o.k === "care" ? "text-[#F43F5E]" : "text-orange"} />
                        <p className="mt-1.5 font-heading text-sm font-bold text-navy">{o.t}</p>
                        <p className="text-[11px] leading-snug text-navy/50">{o.d}</p>
                    </button>
                ))}
            </div>
        </div>
    );
}

function AccountStep({ track, onDone }) {
    const [f, setF] = useState({ name: "", phone: "", password: "", email: "" });
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (f.password.length < 6) return toast.error("Password must be at least 6 characters");
        if (!consent) return toast.error("Please accept the Professional Terms & Privacy Policy");
        setBusy(true);
        try {
            const { data } = await api.post("/pro/register", {
                name: f.name.trim(), phone: f.phone.trim(), password: f.password, email: f.email.trim() || null, track,
            });
            setToken(data.token);
            api.post("/legal/accept", { context: "signup" }).catch(() => {});
            toast.success("Account created — now add your details");
            await onDone();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const fields = [
        { k: "name", label: "Full name", ph: "Ramesh Kumar", type: "text", req: true },
        { k: "phone", label: "Mobile number", ph: "98xxxxxxxx", type: "tel", req: true },
        { k: "password", label: "Create password", ph: "At least 6 characters", type: "password", req: true },
        { k: "email", label: "Email (optional)", ph: "you@example.com", type: "email", req: false },
    ];

    return (
        <form onSubmit={submit} className="space-y-3.5" data-testid="join-account-form">
            {fields.map((x) => (
                <div key={x.k}>
                    <label className="mb-1.5 block text-sm font-semibold text-navy">{x.label}</label>
                    <input data-testid={`join-${x.k}-input`} type={x.type} value={f[x.k]} onChange={set(x.k)} placeholder={x.ph} required={x.req}
                        className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy outline-none transition-colors focus:border-orange" />
                </div>
            ))}
            <label data-testid="join-consent-label" className="flex cursor-pointer items-start gap-3 rounded-xl border border-navy/15 bg-white p-3.5">
                <input data-testid="join-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-orange" />
                <span className="text-xs leading-relaxed text-navy/70">
                    I agree to the HOMIIE{" "}
                    <Link to="/legal/professional-terms" target="_blank" className="font-bold text-orange">Professional Terms</Link> and{" "}
                    <Link to="/legal/privacy-policy" target="_blank" className="font-bold text-orange">Privacy Policy</Link>.
                </span>
            </label>
            <button data-testid="join-account-submit" disabled={busy || !consent} className="flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-bold text-white shadow-lg shadow-orange/30 active:scale-95 disabled:opacity-40">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} CONTINUE
            </button>
        </form>
    );
}

function DetailsStep({ onSubmitted }) {
    const [cats, setCats] = useState([]);
    const [areas, setAreas] = useState([]);
    const [services, setServices] = useState([]);
    const [serviceAreas, setServiceAreas] = useState([]);
    const [experience, setExperience] = useState("");
    const [bio, setBio] = useState("");
    const [docs, setDocs] = useState({}); // type -> {url, file_id, filename}
    const [busy, setBusy] = useState(false);
    const [track, setTrack] = useState("services");
    const [refs, setRefs] = useState([{ name: "", phone: "", relation: "" }, { name: "", phone: "", relation: "" }]);
    const care = track === "care";
    const docDefs = care ? CARE_DOC_DEFS : DOC_DEFS;
    const [loaded, setLoaded] = useState(false);
    const [savedAt, setSavedAt] = useState(null);



    // Auto-save draft (phones often reload the page when the camera/file picker opens)
    useEffect(() => {
        if (!loaded) return;
        const t = setTimeout(() => {
            const documents = Object.entries(docs).map(([type, d]) => ({ type, url: d.url, file_id: d.file_id, filename: d.filename }));
            api.put("/pro/application", { experience, bio, services, service_areas: serviceAreas, documents, submit: false, references: care ? refs : [] })
                .then(() => setSavedAt(new Date())).catch(() => {});
        }, 700);
        return () => { clearTimeout(t); };
    }, [docs, services, serviceAreas, refs, experience, bio]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        api.get("/pro/application").then((r) => {
            const a = r.data;
            if (a.verification_status === "Draft") localStorage.setItem(JOIN_RESUME_KEY, "1");
            else { localStorage.removeItem(JOIN_RESUME_KEY); onSubmitted(); return; }
            setTrack(a.track || "services");
            if (a.services?.length) setServices(a.services);
            if (a.service_areas?.length) setServiceAreas(a.service_areas);
            if (a.experience) setExperience(a.experience);
            if (a.bio) setBio(a.bio);
            if (a.documents?.length) setDocs(Object.fromEntries(a.documents.map((d) => [d.type, d])));
            if (a.references?.length) setRefs([0, 1].map((i) => ({ name: "", phone: "", relation: "", ...(a.references[i] || {}) })));
        }).catch(() => {}).finally(() => setLoaded(true));
    }, []);

    useEffect(() => {
        api.get("/services?all=true").then((r) => {
            const seen = {};
            r.data.forEach((s) => { if (!seen[s.category]) seen[s.category] = s.name.replace(" Visit", ""); });
            setCats(Object.entries(seen).map(([k, v]) => ({ key: k, name: v })));
        }).catch(() => {});
        api.get("/service-areas").then((r) => setAreas(r.data)).catch(() => {});
    }, []);

    const toggle = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

    const save = async (submit) => {
        if (submit) {
            const missing = docDefs.filter((d) => d.required && !docs[d.type]);
            if (missing.length) return toast.error("Please upload: " + missing.map((m) => m.label).join(", "));
            if (!services.length) return toast.error("Select at least one skill");
            if (care && refs.some((r) => !r.name.trim() || !r.phone.trim())) return toast.error("Please add 2 references (name and phone)");
        }
        setBusy(true);
        try {
            const documents = Object.entries(docs).map(([type, d]) => ({ type, url: d.url, file_id: d.file_id, filename: d.filename }));
            await api.put("/pro/application", { experience, bio, services, service_areas: serviceAreas, documents, submit, references: care ? refs : [] });
            toast.success(submit ? "Application submitted for review!" : "Draft saved");
            if (submit) { localStorage.removeItem(JOIN_RESUME_KEY); onSubmitted(); }
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="join-details-form">
            <section>
                <h3 className="mb-2 font-heading text-base font-bold text-navy">{care ? "Your care skills" : "Your skills"}</h3>
                <div className="flex flex-wrap gap-2">
                    {(care ? CARE_SKILLS : cats).map((c) => (
                        <button key={c.key} type="button" data-testid={`skill-${c.key}`} onClick={() => toggle(services, setServices, c.key)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${services.includes(c.key) ? "border-orange bg-orange text-white" : "border-navy/15 text-navy"}`}>
                            {c.name}
                        </button>
                    ))}
                </div>
            </section>

            <section>
                <h3 className="mb-2 font-heading text-base font-bold text-navy">Areas you serve</h3>
                <div className="flex flex-wrap gap-2">
                    {areas.map((a) => {
                        const name = a.cluster || a.name;
                        return (
                            <button key={a.id} type="button" data-testid={`area-${name}`} onClick={() => toggle(serviceAreas, setServiceAreas, name)}
                                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${serviceAreas.includes(name) ? "border-orange bg-orange text-white" : "border-navy/15 text-navy"}`}>
                                {name}
                            </button>
                        );
                    })}
                </div>
            </section>

            <div>
                <label className="mb-1.5 block text-sm font-semibold text-navy">Experience</label>
                <input data-testid="join-experience" value={experience} onChange={(e) => setExperience(e.target.value)} placeholder="e.g. 5 years"
                    className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy outline-none focus:border-orange" />
            </div>
            <div>
                <label className="mb-1.5 block text-sm font-semibold text-navy">About you (optional)</label>
                <textarea data-testid="join-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Tell customers about your work"
                    className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy outline-none focus:border-orange" />
            </div>

            <section>
                <h3 className="mb-1 font-heading text-base font-bold text-navy">Documents (KYC)</h3>
                <p className="mb-3 text-xs text-navy/50">Your documents are private and reviewed only by the HOMIIE verification team.</p>
                <div className="space-y-3">
                    {docDefs.map((d) => (
                        <DocUpload key={d.type} def={d} value={docs[d.type]} onChange={(v) => setDocs((p) => ({ ...p, [d.type]: v }))} onRemove={() => setDocs((p) => { const n = { ...p }; delete n[d.type]; return n; })} />
                    ))}
                </div>
            </section>

            {care && <References refs={refs} setRefs={setRefs} />}

            <div className="rounded-2xl bg-navy/[0.04] p-4 text-xs text-navy/60">
                <div className="flex items-center gap-2 font-semibold text-navy"><ShieldCheck size={15} className="text-orange" /> Two-stage verification</div>
                <p className="mt-1">After you submit, our team reviews your documents and runs police verification before approval. You'll get jobs only after approval.</p>
            </div>

            {savedAt && <p data-testid="join-autosaved" className="text-center text-[11px] font-semibold text-emerald-600">✓ Progress auto-saved at {savedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} — safe to leave and continue later</p>}
            <div className="flex gap-3">
                <button type="button" data-testid="join-save-draft" disabled={busy} onClick={() => save(false)} className="flex-1 rounded-full border border-navy/20 py-3 text-sm font-bold text-navy disabled:opacity-60">Save draft</button>
                <button type="button" data-testid="join-submit-app" disabled={busy} onClick={() => save(true)} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-orange py-3 text-sm font-bold text-white shadow-lg shadow-orange/30 disabled:opacity-60">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} <BadgeCheck size={16} /> SUBMIT
                </button>
            </div>
        </div>
    );
}

function References({ refs, setRefs }) {
    const set = (i, k, v) => setRefs(refs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
    const cls = "w-full rounded-xl border border-navy/15 bg-white px-3 py-2.5 text-sm text-navy outline-none focus:border-orange";
    return (
        <section data-testid="join-references">
            <h3 className="mb-1 font-heading text-base font-bold text-navy">2 References <span className="text-orange">*</span></h3>
            <p className="mb-3 text-xs text-navy/50">Previous employer, family you cared for, or a community member (not relatives). We'll call them.</p>
            {refs.map((r, i) => (
                <div key={i} className="mb-2.5 grid grid-cols-2 gap-2 rounded-2xl border border-navy/10 bg-white p-3">
                    <input data-testid={`ref-${i}-name`} value={r.name} onChange={(e) => set(i, "name", e.target.value)} placeholder={`Reference ${i + 1} name`} className={cls} />
                    <input data-testid={`ref-${i}-phone`} value={r.phone} onChange={(e) => set(i, "phone", e.target.value.replace(/[^\d+]/g, "").slice(0, 13))} placeholder="Phone" className={cls} />
                    <input data-testid={`ref-${i}-relation`} value={r.relation} onChange={(e) => set(i, "relation", e.target.value)} placeholder="How they know you" className={`${cls} col-span-2`} />
                </div>
            ))}
        </section>
    );
}

function DocUpload({ def, value, onChange, onRemove }) {
    const [busy, setBusy] = useState(false);
    const upload = async (e) => {
        const picked = e.target.files?.[0];
        e.target.value = "";
        if (!picked) return;
        setBusy(true);
        try {
            const file = await shrinkImage(picked);
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange({ url: data.url, file_id: data.id, filename: file.name });
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const isImg = value && /\.(png|jpe?g|webp|gif)$/i.test(value.filename || "");
    const src = value ? `${value.url}?auth=${getToken()}` : null;
    return (
        <div className="rounded-2xl border border-navy/10 bg-white p-3" data-testid={`doc-${def.type}`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-semibold text-navy">{def.label}{def.required && <span className="text-orange"> *</span>}</p>
                    <p className="text-xs text-navy/45">{def.hint}</p>
                </div>
                {value ? (
                    <button type="button" data-testid={`doc-remove-${def.type}`} onClick={onRemove} className="flex items-center gap-1 rounded-full bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600"><X size={13} /> Remove</button>
                ) : (
                    <label className="flex cursor-pointer items-center gap-1 rounded-full bg-orange/10 px-3 py-1.5 text-xs font-bold text-orange-dark">
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
                        <input type="file" accept={def.accept} className="hidden" data-testid={`doc-input-${def.type}`} onChange={upload} />
                    </label>
                )}
            </div>
            {value && (
                <div className="mt-2 flex items-center gap-2 rounded-xl bg-navy/[0.03] p-2">
                    {isImg ? <img src={src} alt={def.label} className="h-12 w-12 rounded-lg object-cover" /> : <FileText size={20} className="text-navy/50" />}
                    <span className="flex items-center gap-1 text-xs font-medium text-emerald-700"><Check size={13} /> {value.filename || "Uploaded"}</span>
                </div>
            )}
        </div>
    );
}
